import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import mammoth from 'mammoth';
import './DocumentRepository.css';

const CATEGORIES = [
  { value: 'consent_forms', label: 'Consent Forms' },
  { value: 'client_intake', label: 'Client Intake' },
  { value: 'email_templates', label: 'Email Templates' },
  { value: 'qualifications_licenses', label: 'Qualifications & Licenses' },
  { value: 'cpd_certificates', label: 'CPD Certificates' },
  { value: 'professional_policies', label: 'Professional Policies' },
  { value: 'other', label: 'Other' }
];

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg', '.webp', '.txt'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain'
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const DOCUMENT_LIMITS = { free: 3, basic: 50, pro: 200 };

function DocumentRepository({ user, hasActiveSubscription, subscriptionTier }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [usage, setUsage] = useState({ count: 0, limit: 3, tier: 'free' });
  const [uploadCategory, setUploadCategory] = useState('other');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const tier = subscriptionTier || 'free';
  const limit = DOCUMENT_LIMITS[tier] || DOCUMENT_LIMITS.free;
  const isAtLimit = usage.count >= limit;

  // Fetch documents on mount
  const fetchDocuments = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/documents', {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch documents');

      const data = await response.json();
      setDocuments(data.documents || []);
      setUsage(data.usage || { count: 0, limit, tier });
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [limit, tier]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Auto-clear messages
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // File validation
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type "${ext}" is not supported. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return `File type "${file.type}" is not supported`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large (${formatFileSize(file.size)}). Maximum size is 10MB`;
    }
    if (file.size === 0) {
      return 'File is empty';
    }
    if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
      return 'Invalid filename';
    }
    return null;
  };

  // Upload handler
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    if (isAtLimit) {
      setError('Document limit reached. Upgrade your plan for more storage.');
      return;
    }

    setUploading(true);
    setError('');

    for (const file of files) {
      // Check if adding this file would exceed limit
      if (usage.count + 1 > limit) {
        setError(`Document limit reached (${limit}). Upgrade your plan for more storage.`);
        break;
      }

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        continue;
      }

      try {
        setUploadProgress(`Uploading ${file.name}...`);

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');

        // Build storage path: {user_id}/{category}/{timestamp}-{filename}
        const storagePath = `${user.id}/${uploadCategory}/${Date.now()}-${file.name}`;

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('user-documents')
          .upload(storagePath, file);

        if (uploadError) throw uploadError;

        // Register metadata via API
        const response = await fetch('/api/documents', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filename: file.name,
            category: uploadCategory,
            file_size: file.size,
            mime_type: file.type,
            storage_path: storagePath
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          if (response.status === 429) {
            setError('Document limit reached. Upgrade your plan for more storage.');
            // Clean up storage file
            await supabase.storage.from('user-documents').remove([storagePath]);
            break;
          }
          throw new Error(errorData.error || 'Failed to save document');
        }

        const data = await response.json();
        setDocuments(prev => [data.document, ...prev]);
        setUsage(data.usage);
        setSuccessMessage(`"${file.name}" uploaded successfully`);
      } catch (err) {
        console.error('Upload failed:', err);
        setError(`Failed to upload "${file.name}": ${err.message}`);
      }
    }

    setUploading(false);
    setUploadProgress('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Download handler
  const handleDownload = async (doc) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`/api/documents-download?documentId=${doc.id}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });

      if (!response.ok) throw new Error('Failed to generate download link');

      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document');
    }
  };

  // Delete handler
  const handleDelete = async (doc) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/documents', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ documentId: doc.id })
      });

      if (!response.ok) throw new Error('Failed to delete document');

      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      setUsage(prev => ({ ...prev, count: Math.max(0, prev.count - 1) }));
      setDeleteConfirm(null);
      setSuccessMessage(`"${doc.display_name || doc.filename}" deleted`);
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete document');
    }
  };

  // Rename handler
  const handleRename = async (doc) => {
    if (!renameValue.trim() || renameValue.trim() === (doc.display_name || doc.filename)) {
      setRenamingDoc(null);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch('/api/documents', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ documentId: doc.id, display_name: renameValue.trim() })
      });

      if (!response.ok) throw new Error('Failed to rename document');

      const data = await response.json();
      setDocuments(prev => prev.map(d =>
        d.id === doc.id ? { ...d, display_name: data.document.display_name, updated_at: data.document.updated_at } : d
      ));
      setRenamingDoc(null);
    } catch (err) {
      console.error('Rename failed:', err);
      setError('Failed to rename document');
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  // Preview handler
  const openPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl('');
    setPreviewContent('');
    setPreviewLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const mimeType = doc.mime_type;
      const isDocx = mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isText = mimeType === 'text/plain';

      if (isDocx || isText) {
        // Fetch file binary via signed URL for text-based preview
        const response = await fetch(`/api/documents-download?documentId=${doc.id}&preview=true`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (!response.ok) throw new Error('Failed to generate preview link');
        const { url } = await response.json();

        const fileRes = await fetch(url);
        if (!fileRes.ok) throw new Error('Failed to download file');
        const arrayBuffer = await fileRes.arrayBuffer();

        if (isDocx) {
          // Check ZIP signature — if not valid ZIP, treat as plain text
          const header = new Uint8Array(arrayBuffer.slice(0, 4));
          if (header[0] === 0x50 && header[1] === 0x4B && header[2] === 0x03 && header[3] === 0x04) {
            const result = await mammoth.convertToHtml({ arrayBuffer });
            setPreviewContent(result.value);
          } else {
            const text = new TextDecoder('utf-8').decode(arrayBuffer);
            setPreviewContent(text);
          }
        } else {
          const text = new TextDecoder('utf-8').decode(arrayBuffer);
          setPreviewContent(text);
        }
      } else {
        // PDF and images — use signed URL directly
        const response = await fetch(`/api/documents-download?documentId=${doc.id}&preview=true`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (!response.ok) throw new Error('Failed to generate preview link');
        const data = await response.json();
        setPreviewUrl(data.url);
      }
    } catch (err) {
      console.error('Preview failed:', err);
      setError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewUrl('');
    setPreviewContent('');
  };

  const isPreviewable = (mimeType) => {
    return mimeType === 'application/pdf'
      || mimeType?.startsWith('image/')
      || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      || mimeType === 'text/plain';
  };

  // Check if a document's file type is supported by the compliance checker
  const isCheckerCompatible = (mimeType) => {
    return ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'].includes(mimeType);
  };

  // Navigate to checker with document loaded from storage
  const handleCheckCompliance = (doc) => {
    navigate('/checker', {
      state: {
        fromDocument: {
          id: doc.id,
          filename: doc.filename,
          displayName: doc.display_name || doc.filename,
          mimeType: doc.mime_type,
          storagePath: doc.storage_path,
        }
      }
    });
  };

  // Filter documents by category
  const filteredDocuments = selectedCategory === 'all'
    ? documents
    : documents.filter(d => d.category === selectedCategory);

  // Group documents by category for display
  const getCategoryLabel = (value) => {
    const cat = CATEGORIES.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  if (loading) {
    return (
      <div className="doc-repo">
        <div className="doc-repo-loading">
          <div className="doc-loading-spinner"></div>
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="doc-repo">
      {/* Header */}
      <div className="doc-repo-header">
        <div className="doc-header-info">
          <h2>Document Repository</h2>
          <span className="doc-usage-badge">
            {usage.count} / {limit} documents
          </span>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="doc-message doc-message-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          {error}
        </div>
      )}
      {successMessage && (
        <div className="doc-message doc-message-success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          {successMessage}
        </div>
      )}

      {/* Upload Section */}
      {isAtLimit ? (
        <div className="doc-upgrade-prompt">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="32" height="32">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
          </svg>
          <h3>Document limit reached</h3>
          <p>
            {tier === 'free'
              ? "You've used all 3 of your free document slots. Upgrade to Basic for up to 50 documents."
              : tier === 'basic'
              ? "You've used all 50 of your Basic plan document slots. Upgrade to Pro for up to 200 documents."
              : "You've reached the maximum of 200 documents."}
          </p>
          {tier !== 'pro' && (
            <button className="doc-upgrade-btn" onClick={() => navigate('/pricing')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              {tier === 'free' ? 'Upgrade to Basic' : 'Upgrade to Pro'}
            </button>
          )}
        </div>
      ) : (
        <div className="doc-upload-section">
          <div className="doc-upload-controls">
            <select
              className="doc-category-select"
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value)}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <button
              className="doc-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_EXTENSIONS.join(',')}
              style={{ display: 'none' }}
              onChange={(e) => handleUpload(Array.from(e.target.files))}
            />
          </div>
          <div
            className={`doc-dropzone ${dragActive ? 'doc-dropzone-active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="doc-dropzone-uploading">
                <div className="doc-loading-spinner"></div>
                <p>{uploadProgress}</p>
              </div>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p>Drag and drop files here, or click to browse</p>
                <span className="doc-dropzone-hint">PDF, DOC, DOCX, PNG, JPG, TXT — max 10MB each</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="doc-filters">
        <button
          className={`doc-filter-tab ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          All ({documents.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = documents.filter(d => d.category === cat.value).length;
          if (count === 0) return null;
          return (
            <button
              key={cat.value}
              className={`doc-filter-tab ${selectedCategory === cat.value ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.value)}
            >
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Document List */}
      {filteredDocuments.length === 0 ? (
        <div className="doc-empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
          </svg>
          <p>
            {selectedCategory === 'all'
              ? 'No documents yet. Upload your first document above.'
              : `No ${getCategoryLabel(selectedCategory).toLowerCase()} uploaded yet.`}
          </p>
        </div>
      ) : (
        <div className="doc-grid">
          {filteredDocuments.map(doc => (
            <div key={doc.id} className="doc-card" onClick={() => openPreview(doc)}>
              <div className="doc-card-icon">
                {getFileIcon(doc.mime_type)}
              </div>
              <div className="doc-card-info">
                {renamingDoc === doc.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); handleRename(doc); }} className="doc-rename-form" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRename(doc)}
                      autoFocus
                      className="doc-rename-input"
                    />
                  </form>
                ) : (
                  <span className="doc-card-name" title={doc.display_name || doc.filename}>
                    {doc.display_name || doc.filename}
                  </span>
                )}
                <div className="doc-card-meta">
                  <span className="doc-category-badge">{getCategoryLabel(doc.category)}</span>
                  <span className="doc-card-size">{formatFileSize(doc.file_size)}</span>
                  <span className="doc-card-date">{formatDate(doc.created_at)}</span>
                </div>
              </div>
              <div className="doc-card-actions">
                {isCheckerCompatible(doc.mime_type) && (
                  <button
                    className="doc-action-btn doc-action-btn-check"
                    onClick={(e) => { e.stopPropagation(); handleCheckCompliance(doc); }}
                    title="Check Compliance"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <path d="M9 12l2 2 4-4"/>
                      <path d="M12 3a9 9 0 100 18 9 9 0 000-18z"/>
                    </svg>
                  </button>
                )}
                <button
                  className="doc-action-btn"
                  onClick={(e) => { e.stopPropagation(); setRenamingDoc(doc.id); setRenameValue(doc.display_name || doc.filename); }}
                  title="Rename"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button
                  className="doc-action-btn"
                  onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                  title="Download"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
                {deleteConfirm === doc.id ? (
                  <div className="doc-delete-confirm">
                    <button
                      className="doc-action-btn doc-action-btn-danger"
                      onClick={(e) => { e.stopPropagation(); handleDelete(doc); }}
                      title="Confirm delete"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </button>
                    <button
                      className="doc-action-btn"
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                      title="Cancel"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    className="doc-action-btn doc-action-btn-danger"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(doc.id); }}
                    title="Delete"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <div className="doc-preview-overlay" onClick={closePreview}>
          <div className="doc-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="doc-preview-header">
              <div className="doc-preview-title">
                <span className="doc-preview-name">{previewDoc.display_name || previewDoc.filename}</span>
                <span className="doc-category-badge">{getCategoryLabel(previewDoc.category)}</span>
              </div>
              <button className="doc-preview-close" onClick={closePreview}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="doc-preview-body">
              {previewLoading ? (
                <div className="doc-preview-loading">
                  <div className="doc-loading-spinner"></div>
                  <p>Loading preview...</p>
                </div>
              ) : previewUrl && previewDoc.mime_type === 'application/pdf' ? (
                <iframe
                  src={`${previewUrl}#toolbar=0`}
                  className="doc-preview-iframe"
                  title={previewDoc.filename}
                />
              ) : previewUrl && previewDoc.mime_type?.startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt={previewDoc.filename}
                  className="doc-preview-image"
                />
              ) : previewContent && previewDoc.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' && previewContent.startsWith('<') ? (
                <div className="doc-preview-text" dangerouslySetInnerHTML={{ __html: previewContent }} />
              ) : previewContent ? (
                <pre className="doc-preview-text">{previewContent}</pre>
              ) : (
                <div className="doc-preview-unavailable">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <p>Preview not available for this file type</p>
                  <span className="doc-preview-filetype">{previewDoc.mime_type}</span>
                </div>
              )}
            </div>
            <div className="doc-preview-footer">
              <div className="doc-preview-meta">
                <span>{formatFileSize(previewDoc.file_size)}</span>
                <span>{formatDate(previewDoc.created_at)}</span>
              </div>
              <div className="doc-preview-actions">
                <button
                  className="doc-preview-action-btn"
                  onClick={() => { closePreview(); setRenamingDoc(previewDoc.id); setRenameValue(previewDoc.display_name || previewDoc.filename); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Rename
                </button>
                <button
                  className="doc-preview-action-btn"
                  onClick={() => handleDownload(previewDoc)}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download
                </button>
                <button
                  className="doc-preview-action-btn doc-preview-action-danger"
                  onClick={() => { handleDelete(previewDoc); closePreview(); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper: file type icon
function getFileIcon(mimeType) {
  if (mimeType === 'application/pdf') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="24" height="24">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
    );
  }
  if (mimeType?.startsWith('image/')) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="24" height="24">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
    );
  }
  // Default document icon
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="24" height="24">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

// Helper: format file size
function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Helper: format date
function formatDate(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;

  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

export default DocumentRepository;
